/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description TagEvents
 */
export enum TagEvent {

	/**
	 * @description Dispatched Tag ready (skeleton generated, dependencies loaded)
	 * @type {TagEvent.ON_READY}
	 */
	ON_READY = 'TagEvent.ON_READY',

	/**
	 * @description Dispatched on internet connection ON
	 * @type {TagEvent.ON_ONLINE}
	 */
	ON_ONLINE = 'TagEvent.ON_ONLINE',

	/**
	 * @description Dispatched on internet connection OFF
	 * @type {TagEvent.ON_OFFLINE}
	 */
	ON_OFFLINE = 'TagEvent.ON_OFFLINE',

	/**
	 * @description Dispatched on outer container resize
	 * @type {TagEvent.ON_RESIZE}
	 */
	ON_RESIZE = 'TagEvent.ON_RESIZE',

	/**
	 * @description Dispatched from Model when configs  are loaded and processed
	 * @type {TagEvent.ON_MODEL_READY}
	 */
	ON_MODEL_READY = 'TagEvent.ON_MODEL_READY',

	/**
	 * @description Dispatched from Model if configs load has failed
	 * @type {TagEvent.ON_MODEL_FAILURE}
	 */
	ON_MODEL_FAILURE = 'TagEvent.ON_MODEL_FAILURE',

	/**
	 * @description Dispatched from View when it is ready
	 * @type {TagEvent.ON_VIEW_READY}
	 */
	ON_VIEW_READY = 'TagEvent.ON_VIEW_READY',

	/**
	 * @description Dispatched from Controller when it is ready
	 * @type {TagEvent.ON_CONTROLLER_READY}
	 */
	ON_CONTROLLER_READY = 'TagEvent.ON_CONTROLLER_READY',

	/**
	 * @description Dispatched from Controller on Smart Tag session start  imports load success
	 * @type {TagEvent.ON_SESSION_START_IMPORTS_DONE}
	 */
	ON_SESSION_START_IMPORTS_DONE = 'TagEvent.ON_SESSION_START_IMPORTS_DONE',

	/**
	 * @description Dispatched from Controller on past session start imports load success
	 * @type {TagEvent.ON_IMPORTS_DONE}
	 */
	ON_IMPORTS_DONE = 'TagEvent.ON_IMPORTS_DONE',

	/**
	 * @description Dispatched from Controller on imports load failure
	 * @type {TagEvent.ON_IMPORTS_FAILURE}
	 */
	ON_IMPORTS_FAILURE = 'TagEvent.ON_IMPORTS_FAILURE',

	/**
	 * @description Dispatched from Model after metadata is verified informing that metadata is ready
	 * @type {TagEvent.ON_METADATA_READY}
	 */
	ON_METADATA_READY = 'TagEvent.ON_METADATA_READY',

	/**
	 * @description Dispatched from Model after metadata is verified informing that metadata check or request has failed
	 * @type {TagEvent.ON_METADATA_FAILURE}
	 */
	ON_METADATA_FAILURE = 'TagEvent.ON_METADATA_FAILURE',

	/**
	 * @description Player event wrapped in internal Tag event
	 * @type {TagEvent.ON_PLAYER_EVENT}
	 */
	ON_PLAYER_EVENT = 'TagEvent.ON_PLAYER_EVENT',

	/**
	 * @description Internal Tag BEFORE video player stop request
	 * @type {TagEvent.ON_TAG_BEFORE_PLAYER_STOP_REQUEST}
	 */
	ON_TAG_BEFORE_PLAYER_STOP_REQUEST = 'TagEvent.ON_TAG_BEFORE_PLAYER_STOP_REQUEST',
	/**
	 * @description Internal Tag video player stop request
	 * @type {TagEvent.ON_TAG_PLAYER_STOP_REQUEST}
	 */
	ON_TAG_PLAYER_STOP_REQUEST = 'TagEvent.ON_TAG_PLAYER_STOP_REQUEST',
	/**
	 * @description Internal Tag video player stop success
	 * @type {TagEvent.ON_TAG_PLAYER_STOP_SUCCESS}
	 */
	ON_TAG_PLAYER_STOP_SUCCESS = 'TagEvent.ON_TAG_PLAYER_STOP_SUCCESS',
	/**
	 * @description Internal Tag video player stop failure
	 * @type {TagEvent.ON_TAG_PLAYER_STOP_FAILURE}
	 */
	ON_TAG_PLAYER_STOP_FAILURE = 'TagEvent.ON_TAG_PLAYER_STOP_FAILURE',

	/**
	 * @description Dispatched on before player.stop() by Tag i.e. on API.VIDEO.load() request
	 * @type {TagEvent.ON_API_LOAD_PLAYER_STOP_REQUEST}
	 */
	ON_API_LOAD_PLAYER_STOP_REQUEST = 'TagEvent.ON_API_LOAD_PLAYER_STOP_REQUEST',

	/**
	 * @description Dispatched after successful attempt to stop VTG player from API.VIDEO.load() request
	 * @type {TagEvent.ON_API_LOAD_STOP_PLAYER_SUCCESS}
	 */
	ON_API_LOAD_STOP_PLAYER_SUCCESS = 'TagEvent.ON_API_LOAD_STOP_PLAYER_SUCCESS',

	/**
	 * @description Dispatched on loadVideo request from the page
	 * @type {TagEvent.ON_API_LOAD_VIDEO_REQUEST}
	 */
	ON_API_LOAD_VIDEO_REQUEST = 'TagEvent.ON_API_LOAD_VIDEO_REQUEST',

	/**
	 * @description Request to reset tag
	 * @type {TagEvent.ON_API_RESET_TAG_REQUEST}
	 */
	ON_API_RESET_TAG_REQUEST = 'TagEvent.ON_API_RESET_TAG_REQUEST',

	/**
	 * @description Event informing tag about custom skin application readiness (IMPORTANT: dispatched into tag from a skin application)
	 * @type {TagEvent.ON_CUSTOM_SKIN_READY}
	 */
	ON_CUSTOM_SKIN_READY = 'TagEvent.ON_CUSTOM_SKIN_READY',

	/**
	 * @description Request to create instance of player
	 * @type {TagEvent.ON_CREATE_PLAYER_REQUEST}
	 */
	ON_CREATE_PLAYER_REQUEST = 'TagEvent.ON_CREATE_PLAYER_REQUEST',

	/**
	 * @description On player instance creation success
	 * @type {TagEvent.ON_CREATE_PLAYER_SUCCESS}
	 */
	ON_CREATE_PLAYER_SUCCESS = 'TagEvent.ON_CREATE_PLAYER_SUCCESS',

	/**
	 * @description On player instance creation failure
	 * @type {TagEvent.ON_CREATE_PLAYER_FAILURE}
	 */
	ON_CREATE_PLAYER_FAILURE = 'TagEvent.ON_CREATE_PLAYER_FAILURE',

	/**
	 * @description Request to destroy instance of player
	 * @type {TagEvent.ON_DESTROY_PLAYER_REQUEST}
	 */
	ON_DESTROY_PLAYER_REQUEST = 'TagEvent.ON_DESTROY_PLAYER_REQUEST',

	/**
	 * @description On destroy player instance success
	 * @type {TagEvent.ON_DESTROY_PLAYER_SUCCESS}
	 */
	ON_DESTROY_PLAYER_SUCCESS = 'TagEvent.ON_DESTROY_PLAYER_SUCCESS',

	/**
	 * @description On destroy player instance failure
	 * @type {TagEvent.ON_DESTROY_PLAYER_FAILURE}
	 */
	ON_DESTROY_PLAYER_FAILURE = 'TagEvent.ON_DESTROY_PLAYER_FAILURE',

	/**
	 * @description Informational event - resource loading started
	 * @type {TagEvent.ON_RESOURCE_LOAD_STARTED}
	 */
	ON_RESOURCE_LOAD_STARTED = 'TagEvent.ON_RESOURCE_LOAD_STARTED',

	/**
	 * @description Informational event - resource loading completed successfully
	 * @type {TagEvent.ON_RESOURCE_LOAD_COMPLETED}
	 */
	ON_RESOURCE_LOAD_COMPLETED = 'TagEvent.ON_RESOURCE_LOAD_COMPLETED',

	/**
	 * @description Informational event - resource loading failed
	 * @type {TagEvent.ON_RESOURCE_LOAD_FAILED}
	 */
	ON_RESOURCE_LOAD_FAILED = 'TagEvent.ON_RESOURCE_LOAD_FAILED',

	/**
	 * @description Internal Tag fatal error event
	 * @type {TagEvent.ON_FATAL_ERROR}
	 */
	ON_FATAL_ERROR = 'TagEvent.ON_FATAL_ERROR',

	/**
	 * @description Internal Tag ad blocker detection event
	 * @type {TagEvent.ON_AD_BLOCKER_DETECTED}
	 */
	ON_AD_BLOCKER_DETECTED = 'TagEvent.ON_AD_BLOCKER_DETECTED',

	/**
	 * @description Event informing tag about request to stop ad blocker check
	 * @type {TagEvent.ON_REQUEST_AD_BLOCKER_CHECK_STOP}
	 */
	ON_REQUEST_AD_BLOCKER_CHECK_STOP = 'TagEvent.ON_REQUEST_AD_BLOCKER_CHECK_STOP',

	/**
	 * @description Request to clear error overlay
	 * @type {TagEvent.ON_REQUEST_TO_CLEAR_ERROR}
	 */
	ON_REQUEST_TO_CLEAR_ERROR = 'TagEvent.ON_REQUEST_TO_CLEAR_ERROR',

	/**
	 * @description Dispatched when SmartTag begins to load dependencies
	 * @type {TagEvent.ON_START_DEPENDENCIES_LOAD}
	 */
	ON_START_DEPENDENCIES_LOAD = 'TagEvent.ON_START_DEPENDENCIES_LOAD',

	/**
	 * @description Dispatched when requesting to register a hook with player
	 * @type {TagEvent.ON_PLAYER_HOOK_REQUEST_REGISTER}
	 */
	ON_PLAYER_HOOK_REQUEST_REGISTER = 'TagEvent.ON_PLAYER_HOOK_REQUEST_REGISTER',

	/**
	 * @description Dispatched when hook is registered with avia player
	 * @type {TagEvent.ON_PLAYER_HOOK_REGISTERED}
	 */
	ON_PLAYER_HOOK_REGISTERED = 'TagEvent.ON_PLAYER_HOOK_REGISTERED',

	/**
	 * @description Dispatched when avia player executes hook callback
	 * @type {TagEvent.ON_PLAYER_HOOK_EXECUTED}
	 */
	ON_PLAYER_HOOK_EXECUTED = 'TagEvent.ON_PLAYER_HOOK_EXECUTED',

	/**
	 * @description Dispatched when requesting to remove a hook from player
	 * @type {TagEvent.ON_PLAYER_HOOK_REQUEST_REMOVE}
	 */
	ON_PLAYER_HOOK_REQUEST_REMOVE = 'TagEvent.ON_PLAYER_HOOK_REQUEST_REMOVE',

	/**
	 * @description Dispatched when hook is removed from avia player
	 * @type {TagEvent.ON_PLAYER_HOOK_REMOVED}
	 */
	ON_PLAYER_HOOK_REMOVED = 'TagEvent.ON_PLAYER_HOOK_REMOVED',

	/**
	 * @description Dispatched before resource load it requested
	 * @type {TagEvent.ON_BEFORE_RESOURCE_LOAD}
	 */
	ON_BEFORE_RESOURCE_LOAD = 'TagEvent.ON_BEFORE_RESOURCE_LOAD',

	/**
	 * @description Dispatched when Nerd Mode diagnostic overlay visibility changes
	 * Fires on show/hide via button click, keyboard shortcut (Ctrl+Shift+N), or API call
	 * @type {TagEvent.ON_NERD_MODE_VISIBILITY_CHANGE}
	 */
	ON_NERD_MODE_VISIBILITY_CHANGE = 'TagEvent.ON_NERD_MODE_VISIBILITY_CHANGE',

	/**
	 * @description Dispatched when Nerd Mode verbose mode (extended metrics) state changes
	 * Fires on setVerbose/toggleVerbose via button click, keyboard shortcut (Ctrl+Shift+V), or API call
	 * @type {TagEvent.ON_NERD_MODE_VERBOSE_CHANGE}
	 */
	ON_NERD_MODE_VERBOSE_CHANGE = 'TagEvent.ON_NERD_MODE_VERBOSE_CHANGE',

	/**
	 * @description Dispatched once per player, right after the Nerd Mode manager initializes —
	 * the earliest point event capture can run. Hosts that cannot set the `?nerdcapture=1` URL
	 * param (e.g. CTV apps) can listen for this and call `tag.API.TAG.NerdMode.startCapture()`
	 * to begin silent capture at the earliest moment. `data.nerdModeCapturing` indicates whether
	 * capture already auto-started (via URL/config), so the listener can skip a redundant call.
	 * @type {TagEvent.ON_NERD_MODE_CAPTURE_READY}
	 */
	ON_NERD_MODE_CAPTURE_READY = 'TagEvent.ON_NERD_MODE_CAPTURE_READY',

	/**
	 * @description Dispatched when Nerd Mode's "expand all JSON nodes" state changes
	 * Fires on setExpandAllNodes() via API call, `?nerdexpand=1` URL param, or `onStart.expandAll`
	 * config. `data.nerdModeExpandAll` carries the new state.
	 * @type {TagEvent.ON_NERD_MODE_EXPAND_ALL_CHANGE}
	 */
	ON_NERD_MODE_EXPAND_ALL_CHANGE = 'TagEvent.ON_NERD_MODE_EXPAND_ALL_CHANGE',

	/**
	 * @description Dispatched when Nerd Mode's attached/detached (viewport-pinned)
	 * positioning state changes. Fires on `toggleDetached()` via the header button or API
	 * call — not on the initial `?nerddetached` / `onStart.detached` startup resolution,
	 * which has no prior state to change from. `data.nerdModeDetached` carries the new state.
	 * @type {TagEvent.ON_NERD_MODE_DETACHED_CHANGE}
	 */
	ON_NERD_MODE_DETACHED_CHANGE = 'TagEvent.ON_NERD_MODE_DETACHED_CHANGE',

	/**
	 * @description Dispatched when a host-injected "dumb container" Nerd Mode tab
	 * (`registerContainerTab()`) becomes the visible tab — on tab selection, and on
	 * overlay show while the container tab is already active. `data.nerdModeContainerTabLabel`
	 * carries the tab's label. Fires alongside the tab's own `onActivate()` callback —
	 * listen here only if a callback reference isn't convenient at the call site.
	 * @type {TagEvent.ON_NERD_MODE_CONTAINER_TAB_ACTIVATED}
	 */
	ON_NERD_MODE_CONTAINER_TAB_ACTIVATED = 'TagEvent.ON_NERD_MODE_CONTAINER_TAB_ACTIVATED',

	/**
	 * @description Dispatched when a host-injected "dumb container" Nerd Mode tab
	 * (`registerContainerTab()`) stops being the visible tab — on switching to a
	 * different tab, and on overlay hide while the container tab is active.
	 * `data.nerdModeContainerTabLabel` carries the tab's label. Fires alongside the tab's
	 * own `onDeactivate()` callback — listen here only if a callback reference isn't
	 * convenient at the call site.
	 * @type {TagEvent.ON_NERD_MODE_CONTAINER_TAB_DEACTIVATED}
	 */
	ON_NERD_MODE_CONTAINER_TAB_DEACTIVATED = 'TagEvent.ON_NERD_MODE_CONTAINER_TAB_DEACTIVATED',

	/**
	 * @description Dispatched when Cast mode starts (playback moves to Chromecast device)
	 * Fired when user starts casting and playback mode switches to CHROMECAST
	 * @type {TagEvent.ON_CAST_MODE_STARTED}
	 */
	ON_CAST_MODE_STARTED = 'TagEvent.ON_CAST_MODE_STARTED',

	/**
	 * @description Dispatched when Cast mode ends (playback returns to local player)
	 * Fired when casting stops or encounters error and playback mode returns to INTERNAL
	 * @type {TagEvent.ON_CAST_MODE_ENDED}
	 */
	ON_CAST_MODE_ENDED = 'TagEvent.ON_CAST_MODE_ENDED',

	/**
	 * @description Dispatched when the user taps the replay overlay after stitcher VOD ends on mobile web.
	 * The page must handle this event by refreshing credentials (JWT, DRM) and calling load()
	 * to start a clean session — Smart Tag does not call load() internally because the JWT and
	 * DRM license may have expired by RESOURCE_END and re-entrancy into load() is unsafe.
	 *
	 * @type {TagEvent.ON_MOBILE_WEB_REPLAY_REQUEST}
	 */
	ON_MOBILE_WEB_REPLAY_REQUEST = 'TagEvent.ON_MOBILE_WEB_REPLAY_REQUEST',

	/**
	 * @description Dispatched after a successful tag.API.TAG.updateConfig() call has merged
	 * a whitelisted partial into tag.model.config. Event data carries an IConfigUpdatedEventData
	 * diff payload (paths + per-path before/after). Effective on next player rebuild —
	 * plugins re-read tag.model.config via shouldBeUsed() during #atomicPlayerReplacement.
	 *
	 * @type {TagEvent.ON_CONFIG_UPDATED}
	 */
	ON_CONFIG_UPDATED = 'TagEvent.ON_CONFIG_UPDATED',

	/**
	 * @description Dispatched when an accepted `tag.API.TAG.uiMode()` call kicks off a UI mode
	 * transition. Payload: `{ uiMode: UIMode }`. Bridged into Skin as
	 * `SkinEvent.ON_UI_MODE_CHANGE_REQUEST` by `Skin.ts.onTagEvent`. Re-entrancy: blocked
	 * by the in-flight lock; no further REQUEST fires until DONE.
	 *
	 * @type {TagEvent.ON_UI_MODE_CHANGE_REQUEST}
	 */
	ON_UI_MODE_CHANGE_REQUEST = 'TagEvent.ON_UI_MODE_CHANGE_REQUEST',

	/**
	 * @description Dispatched after Skin calls `tag.notifyUiModeChangeDone(mode)` (overlay
	 * swap and any CSS transition committed), or synthesized synchronously by Tag when no
	 * Skin is attached. Payload: `{ uiMode: UIMode }`. Releases the in-flight lock.
	 *
	 * @type {TagEvent.ON_UI_MODE_CHANGE_DONE}
	 */
	ON_UI_MODE_CHANGE_DONE = 'TagEvent.ON_UI_MODE_CHANGE_DONE'
}

/**
 * @description Array of events for bulk subscription
 * @return {string[]}
 */
export const tagEvents = (): TagEvent[] => {
	const out: TagEvent[] = [];
	Object.keys( TagEvent ).forEach(
		( key ) => {
			out.push( TagEvent[ key ] );
		}
	);
	return out;
};